import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Breadcrumb, BreadcrumbItem, Button } from 'reactstrap';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFolder,
    faFile,
    faTrash,
    faCheckCircle,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { DownloadVDRFile } from '../../../slices/marketplace/data-room/thunks/download-vdr-file.thunk';
import { DownloadVDRFiles } from '../../../slices/marketplace/data-room/thunks/download-vdr-files.thunk';
import { RemoveVDRFile } from '../../../slices/marketplace/data-room/thunks/remove-vdr-file.thunk';
import { UploadVDRFile } from '../../../slices/marketplace/data-room/thunks/upload-vdr-file.thunk';
import { ApproveVDRFileUpload } from '../../../slices/marketplace/data-room/thunks/approve-vdr-file-upload.thunk';
import { RejectVDRFileUpload } from '../../../slices/marketplace/data-room/thunks/reject-vdr-file-upload.thunk';
import { CancelVDRFileUpload } from '../../../slices/marketplace/data-room/thunks/cancel-vdr-file-upload.thunk';
import { ApproveVDRFileRemoval } from '../../../slices/marketplace/data-room/thunks/approve-vdr-file-removal.thunk';
import { RejectVDRFileRemoval } from '../../../slices/marketplace/data-room/thunks/reject-vdr-file-removal.thunk';
import { CancelVDRFileRemoval } from '../../../slices/marketplace/data-room/thunks/cancel-vdr-file-removal.thunk';
import { VDRFolderView } from '../../../slices/marketplace/data-room/models/vdr-folder-view.model';
import { VDRFileView } from '../../../slices/marketplace/data-room/models/vdr-file-view.model';
import { VDRFileSourceType } from '../../../slices/marketplace/data-room/models/vdr-file-source-type.enum';
import { VDRDocumentSelectionHelper } from './VDRDocumentSelectionHelper';
import { ShowFirstThenFullTextOnHoverLabel } from '../../../components/ShowFirstThenFullTextOnHoverLabel/ShowFirstThenFullTextOnHoverLabel';
import {
    VDRFolderNavigationHelper,
    BreadcrumbItem as NavigationBreadcrumbItem,
} from './VDRFolderNavigationHelper';
import createNotification from '../../../utils/createNotification';
import {
    DataRoomAccessRightsPopup,
    DataRoomTeamMember,
} from '../../../components/DataRoomAccessRightsPopup/DataRoomAccessRightsPopup';
import './DataRoom.scss';
import { EngagementPractice } from '../../../slices/marketplace/timeline/models/engagement-practice';
import { VDRAccessPermission } from '../../../slices/marketplace/data-room/models/vdr-access-permission.model';
import Helpers from '../../../utils/helper';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import VDRUploadFileModal from './VDRUploadFileModal/VDRUploadFileModal';
import {
    VDRFileStatus,
    getFileStatus,
    getFileId,
    isPendingUploadApproval,
    isPendingRemovalApproval,
    isApprovedFile,
} from './VDRFileStatusHelper';

interface DataRoomDisplayItem {
    id: string;
    name: string;
    type: 'folder' | 'file';
    size: number;
    fullPath: string;
    fileView?: VDRFileView;
    permissions: VDRAccessPermission[];
}

export interface DataRoomProps {
    engagementId: number;
    rootFolder?: VDRFolderView;
    practices: EngagementPractice[];
    teamMembers?: DataRoomTeamMember[];
    memberOwnerId: number;
    onPermissionsUpdated?: () => void;
    onFileRemoved?: () => void;
    canUserManagePermissions?: boolean;
    isReadonly?: boolean;
}

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 MB';
    return Helpers.formatBytes(bytes);
};

const DataRoom: React.FC<DataRoomProps> = ({
    engagementId,
    rootFolder,
    practices,
    teamMembers = [],
    memberOwnerId: buyerUserId,
    onPermissionsUpdated,
    onFileRemoved,
    canUserManagePermissions = false,
    isReadonly,
}) => {
    const dispatch = useDispatch() as any;
    const party = useCurrentMarketplaceParty(engagementId);
    const canUserRemoveFiles =
        party === TimelineParty.VetValue ||
        (!isReadonly &&
            (party === TimelineParty.Broker || party === TimelineParty.Seller));

    const [currentFolderPath, setCurrentFolderPath] = useState<string>('');
    const [breadcrumb, setBreadcrumb] = useState<NavigationBreadcrumbItem[]>(
        []
    );
    const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isAccessRightsPopupVisible, setIsAccessRightsPopupVisible] =
        useState<boolean>(false);
    const [selectedItemForAccessRights, setSelectedItemForAccessRights] =
        useState<DataRoomDisplayItem | null>(null);
    const [isRemoveConfirmationVisible, setIsRemoveConfirmationVisible] =
        useState<boolean>(false);
    const [fileToRemove, setFileToRemove] = useState<VDRFileView | null>(null);
    const [isUploadModalVisible, setIsUploadModalVisible] =
        useState<boolean>(false);
    const [isProcessingAction, setIsProcessingAction] =
        useState<boolean>(false);

    const isAdmin = party === TimelineParty.VetValue;
    const isBroker = party === TimelineParty.Broker;
    const isSeller = party === TimelineParty.Seller;
    const canApproveReject = isAdmin || isBroker;

    const navigationHelper = useMemo(
        () => new VDRFolderNavigationHelper(rootFolder),
        [rootFolder]
    );

    const selectionHelper = useMemo(
        () => new VDRDocumentSelectionHelper(rootFolder, selectedPaths),
        [rootFolder, selectedPaths]
    );

    const getCurrentFolder = useCallback((): VDRFolderView | undefined => {
        return navigationHelper.findFolderByPath(currentFolderPath);
    }, [navigationHelper, currentFolderPath]);

    const calculateFolderSize = useCallback((folder: VDRFolderView): number => {
        let totalSize = 0;

        folder.files.forEach((file: VDRFileView) => {
            totalSize += file.fileSize;
        });

        folder.subFolders.forEach((subFolder: VDRFolderView) => {
            totalSize += calculateFolderSize(subFolder);
        });

        return totalSize;
    }, []);

    const getCurrentFolderItems = useCallback((): DataRoomDisplayItem[] => {
        const currentFolder = getCurrentFolder();
        if (!currentFolder) {
            return [];
        }

        const items: DataRoomDisplayItem[] = [];

        currentFolder.subFolders.forEach((subFolder: VDRFolderView) => {
            items.push({
                id: subFolder.fullPath,
                name: subFolder.name,
                type: 'folder',
                size: calculateFolderSize(subFolder),
                fullPath: subFolder.fullPath,
                permissions: subFolder.vdrAccessPermission ?? [],
            });
        });

        currentFolder.files.forEach((file: VDRFileView) => {
            items.push({
                id: file.fileUrl,
                name: file.name,
                type: 'file',
                size: file.fileSize,
                fullPath: file.fileUrl,
                fileView: file,
                permissions: file.vdrAccessPermission ?? [],
            });
        });

        return items;
    }, [getCurrentFolder, calculateFolderSize]);

    const handleSelectAll = (
        isSelect: boolean,
        rows: DataRoomDisplayItem[]
    ) => {
        const currentFolder = getCurrentFolder();
        const items = rows.map((row) => ({
            fullPath: row.fullPath,
            type: row.type,
        }));

        setSelectedPaths((prevSelectedPaths) => {
            const tempHelper = new VDRDocumentSelectionHelper(
                rootFolder,
                prevSelectedPaths
            );
            return tempHelper.handleSelectAll(
                items,
                isSelect,
                currentFolder?.fullPath
            );
        });
    };

    const handleDownload = useCallback(async () => {
        const currentHelper = new VDRDocumentSelectionHelper(
            rootFolder,
            selectedPaths
        );
        const files = currentHelper.getSelectedFiles();
        if (files.length === 0) {
            createNotification('No files selected for download', 'warning');
            return;
        }

        if (!engagementId || isNaN(engagementId)) {
            createNotification('Engagement ID is missing', 'error');
            return;
        }

        const fileDownloadInfos = files
            .map((file: VDRFileView) => {
                const fileId = getFileId(file);
                if (!fileId) return null;
                return {
                    fileId,
                    fileSourceType: file.fileSourceType,
                };
            })
            .filter(
                (
                    info
                ): info is {
                    fileId: number;
                    fileSourceType: VDRFileSourceType;
                } => info !== null
            );

        if (fileDownloadInfos.length === 0) {
            createNotification(
                'Selected files are missing source information. Cannot download.',
                'error'
            );
            return;
        }

        if (fileDownloadInfos.length !== files.length) {
            createNotification(
                'Some selected files are missing source information and will be skipped.',
                'warning'
            );
        }

        setIsDownloading(true);
        try {
            const blob = await dispatch(
                DownloadVDRFiles({
                    engagementId: engagementId,
                    files: fileDownloadInfos,
                })
            ).unwrap();

            const url = window.URL.createObjectURL(new Blob([blob]));
            const fileName =
                fileDownloadInfos.length === 1
                    ? files[0]?.name || 'file'
                    : 'VDRFiles.zip';
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setSelectedPaths(new Set());
        } catch (error) {
            console.error('Failed to download files:', error);
        } finally {
            setIsDownloading(false);
        }
    }, [dispatch, engagementId, selectionHelper]);

    const handleFileClick = useCallback(
        async (file: VDRFileView) => {
            if (!engagementId || isNaN(engagementId)) {
                return;
            }

            const fileId = getFileId(file);
            if (!fileId) {
                createNotification(
                    'File source information is missing. Cannot download file.',
                    'error'
                );
                return;
            }

            try {
                const result = await dispatch(
                    DownloadVDRFile({
                        engagementId: engagementId,
                        fileId: fileId,
                        fileSourceType: file.fileSourceType,
                    })
                ).unwrap();
                window.open(result, '_blank');
            } catch (error) {
                console.error('Failed to download file:', error);
            }
        },
        [dispatch, engagementId]
    );

    const showRemoveConfirmation = (file: VDRFileView) => {
        setFileToRemove(file);
        setIsRemoveConfirmationVisible(true);
    };

    const handleRemoveFile = async () => {
        if (!fileToRemove) return;
        setIsProcessingAction(true);
        try {
            await dispatch(
                RemoveVDRFile({
                    engagementId: engagementId,
                    fileId: getFileId(fileToRemove),
                    fileSourceType: fileToRemove.fileSourceType,
                })
            );
            setIsRemoveConfirmationVisible(false);
            onFileRemoved?.();
        } finally {
            setIsProcessingAction(false);
        }
    };

    const handleUploadFile = async (
        temporaryFileId: number,
        folderPath: string,
        fileName: string
    ) => {
        await dispatch(
            UploadVDRFile({
                engagementId: engagementId,
                temporaryFileId,
                folderPath,
                fileName,
            })
        );
        onFileRemoved?.();
    };

    const handleApproveUpload = async (file: VDRFileView) => {
        setIsProcessingAction(true);
        try {
            await dispatch(
                ApproveVDRFileUpload({
                    engagementId: engagementId,
                    fileId: getFileId(file),
                })
            );
            onFileRemoved?.();
        } finally {
            setIsProcessingAction(false);
        }
    };

    const handleRejectUpload = async (file: VDRFileView) => {
        setIsProcessingAction(true);
        try {
            await dispatch(
                RejectVDRFileUpload({
                    engagementId: engagementId,
                    fileId: getFileId(file),
                })
            );
            onFileRemoved?.();
        } finally {
            setIsProcessingAction(false);
        }
    };

    const handleCancelUpload = async (file: VDRFileView) => {
        setIsProcessingAction(true);
        try {
            await dispatch(
                CancelVDRFileUpload({
                    engagementId: engagementId,
                    fileId: getFileId(file),
                })
            );
            onFileRemoved?.();
        } finally {
            setIsProcessingAction(false);
        }
    };

    const handleApproveRemoval = async (file: VDRFileView) => {
        setIsProcessingAction(true);
        try {
            await dispatch(
                ApproveVDRFileRemoval({
                    engagementId: engagementId,
                    fileId: getFileId(file),
                    fileSourceType: file.fileSourceType,
                })
            );
            onFileRemoved?.();
        } finally {
            setIsProcessingAction(false);
        }
    };

    const handleRejectRemoval = async (file: VDRFileView) => {
        setIsProcessingAction(true);
        try {
            await dispatch(
                RejectVDRFileRemoval({
                    engagementId: engagementId,
                    fileId: getFileId(file),
                    fileSourceType: file.fileSourceType,
                })
            );
            onFileRemoved?.();
        } finally {
            setIsProcessingAction(false);
        }
    };

    const handleCancelRemoval = async (file: VDRFileView) => {
        setIsProcessingAction(true);
        try {
            await dispatch(
                CancelVDRFileRemoval({
                    engagementId: engagementId,
                    fileId: getFileId(file),
                    fileSourceType: file.fileSourceType,
                })
            );
            onFileRemoved?.();
        } finally {
            setIsProcessingAction(false);
        }
    };

    const navigateToFolder = useCallback(
        (folderPath: string) => {
            const folder = navigationHelper.findFolderByPath(folderPath);
            if (folder) {
                setCurrentFolderPath(folderPath);
                const breadcrumbItems =
                    navigationHelper.buildBreadcrumbFromPath(folderPath);
                setBreadcrumb(breadcrumbItems);
            }
        },
        [navigationHelper]
    );

    const handleBreadcrumbClick = useCallback(
        (folderPath: string) => {
            navigateToFolder(folderPath);
        },
        [navigateToFolder]
    );

    useEffect(() => {
        if (rootFolder) {
            if (!currentFolderPath) {
                setCurrentFolderPath(rootFolder.fullPath);
            }
            const breadcrumbItems = navigationHelper.buildBreadcrumbFromPath(
                currentFolderPath || rootFolder.fullPath
            );
            setBreadcrumb(breadcrumbItems);
        }
    }, [rootFolder, navigationHelper, currentFolderPath]);

    const getSortedItems = (
        items: DataRoomDisplayItem[],
        sortField?: string,
        sortOrder?: 'asc' | 'desc'
    ): DataRoomDisplayItem[] => {
        const folders = items.filter(
            (item: DataRoomDisplayItem) => item.type === 'folder'
        );
        const files = items.filter(
            (item: DataRoomDisplayItem) => item.type === 'file'
        );

        const sortItems = (itemsToSort: DataRoomDisplayItem[]) => {
            if (!sortField) {
                return itemsToSort;
            }

            const sorted = [...itemsToSort].sort((a, b) => {
                let aValue: any;
                let bValue: any;

                if (sortField === 'name') {
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                } else if (sortField === 'size') {
                    aValue = a.size;
                    bValue = b.size;
                } else {
                    return 0;
                }

                if (aValue < bValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                return 0;
            });

            return sorted;
        };

        const sortedFolders = sortItems(folders);
        const sortedFiles = sortItems(files);

        return [...sortedFolders, ...sortedFiles];
    };

    const [sortState, setSortState] = useState<{
        sortField?: string;
        sortOrder?: 'asc' | 'desc';
    }>({});

    const currentItems = useMemo(() => {
        const items = getCurrentFolderItems();
        return getSortedItems(items, sortState.sortField, sortState.sortOrder);
    }, [getCurrentFolderItems, sortState]);

    const isAllSelected = useMemo(() => {
        if (currentItems.length === 0) return false;
        return currentItems.every((item: DataRoomDisplayItem) =>
            selectedPaths.has(item.fullPath)
        );
    }, [currentItems, selectedPaths]);

    const isIndeterminate = useMemo(() => {
        if (currentItems.length === 0) return false;
        const selectedCount = currentItems.filter((item: DataRoomDisplayItem) =>
            selectedPaths.has(item.fullPath)
        ).length;
        return selectedCount > 0 && selectedCount < currentItems.length;
    }, [currentItems, selectedPaths]);

    const handleTableChange = (type: any, newState: any) => {
        if (type === 'sort') {
            const { sortField, sortOrder } = newState;
            setSortState({
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
        }
    };

    const handleTeamAccessRightsClick = useCallback(
        (row: DataRoomDisplayItem) => {
            setSelectedItemForAccessRights(row);
            setIsAccessRightsPopupVisible(true);
        },
        []
    );

    const handleCloseAccessRightsPopup = useCallback(() => {
        setIsAccessRightsPopupVisible(false);
        setSelectedItemForAccessRights(null);
    }, []);

    const getFolderPathForDisplay = useCallback((): string => {
        if (!selectedItemForAccessRights) return '';

        const pathParts = selectedItemForAccessRights.fullPath
            .split('/')
            .filter((part) => part !== '');

        return pathParts.join(' > ');
    }, [selectedItemForAccessRights]);

    const getSubFoldersList = useCallback((): string[] => {
        if (
            !selectedItemForAccessRights ||
            selectedItemForAccessRights.type !== 'folder'
        ) {
            return [];
        }

        const folder = navigationHelper.findFolderByPath(
            selectedItemForAccessRights.fullPath
        );
        if (!folder) return [];

        return folder.subFolders.map((subFolder) => subFolder.name);
    }, [selectedItemForAccessRights, navigationHelper]);

    const getStatusText = (file: VDRFileView) => {
        const status = getFileStatus(file);
        switch (status) {
            case VDRFileStatus.Removed:
                return 'Removed';
            case VDRFileStatus.UnderReviewUpload:
                return 'Under Review (Upload)';
            case VDRFileStatus.UnderReviewRemoval:
                return 'Under Review (Removal)';
            case VDRFileStatus.Approved:
            default:
                return 'Approved';
        }
    };

    const renderFileActions = (file: VDRFileView) => {
        if (file.isHiddenForExternalSources) {
            return null;
        }

        const pendingUpload = isPendingUploadApproval(file);
        const pendingRemoval = isPendingRemovalApproval(file);
        const approved = isApprovedFile(file);

        if (pendingUpload) {
            if (canApproveReject) {
                return (
                    <div className="vdr-action-buttons">
                        <span
                            className="btn btn-icon"
                            onClick={() => handleApproveUpload(file)}>
                            <FontAwesomeIcon
                                style={{ color: 'green' }}
                                icon={faCheckCircle}
                                title="Approve Upload"
                            />
                        </span>
                        <span
                            className="btn btn-icon"
                            onClick={() => handleRejectUpload(file)}>
                            <FontAwesomeIcon
                                style={{ color: 'red' }}
                                icon={faTimes}
                                title="Reject Upload"
                            />
                        </span>
                    </div>
                );
            }
            if (isSeller && !isReadonly) {
                return (
                    <div className="vdr-action-buttons">
                        <span
                            className="btn btn-icon"
                            onClick={() => handleCancelUpload(file)}>
                            <FontAwesomeIcon
                                style={{ color: 'red' }}
                                icon={faTimes}
                                title="Cancel Upload"
                            />
                        </span>
                    </div>
                );
            }
        }

        if (pendingRemoval) {
            if (canApproveReject) {
                return (
                    <div className="vdr-action-buttons">
                        <span
                            className="btn btn-icon"
                            onClick={() => handleApproveRemoval(file)}>
                            <FontAwesomeIcon
                                style={{ color: 'green' }}
                                icon={faCheckCircle}
                                title="Approve Removal"
                            />
                        </span>
                        <span
                            className="btn btn-icon"
                            onClick={() => handleRejectRemoval(file)}>
                            <FontAwesomeIcon
                                style={{ color: 'red' }}
                                icon={faTimes}
                                title="Reject Removal"
                            />
                        </span>
                    </div>
                );
            }
            if (isSeller && !isReadonly) {
                return (
                    <div className="vdr-action-buttons">
                        <span
                            className="btn btn-icon"
                            onClick={() => handleCancelRemoval(file)}>
                            <FontAwesomeIcon
                                style={{ color: 'red' }}
                                icon={faTimes}
                                title="Cancel Removal Request"
                            />
                        </span>
                    </div>
                );
            }
        }

        if (approved && canUserRemoveFiles) {
            return (
                <div className="vdr-action-buttons">
                    <span
                        className="btn btn-icon"
                        onClick={() => showRemoveConfirmation(file)}>
                        <FontAwesomeIcon
                            style={{ color: 'red' }}
                            icon={faTrash}
                            title="Remove File"
                        />
                    </span>
                </div>
            );
        }

        return null;
    };

    const columns = [
        {
            dataField: 'name',
            text: 'Name',
            headerClasses: 'name-column-header',
            sort: true,
            sortFunc: () => {
                return 0;
            },
            formatter: (cellContent: string, row: DataRoomDisplayItem) => {
                return (
                    <div
                        className="data-room-item-name"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                        }}
                        onClick={(e) => {
                            if ((e.target as HTMLElement).closest('input')) {
                                return;
                            }
                            if (row.type === 'folder') {
                                navigateToFolder(row.fullPath);
                            } else if (row.fileView) {
                                handleFileClick(row.fileView);
                            }
                        }}>
                        <FontAwesomeIcon
                            icon={row.type === 'folder' ? faFolder : faFile}
                            style={{
                                color: '#000',
                            }}
                        />
                        <span>{cellContent}</span>
                    </div>
                );
            },
        },
        {
            dataField: 'size',
            text: 'Size',
            headerClasses: 'size-column-header',
            sort: true,
            sortFunc: () => {
                return 0;
            },
            formatter: (cellContent: number) => {
                return <div>{formatFileSize(cellContent)}</div>;
            },
        },
        {
            dataField: 'status',
            text: 'Status',
            headerClasses: 'status-column-header',
            sort: false,
            formatter: (cellContent: any, row: DataRoomDisplayItem) => {
                if (row.type === 'file' && row.fileView) {
                    return getStatusText(row.fileView);
                }
                return null;
            },
        },
        {
            dataField: 'action',
            text: 'Action',
            headerClasses: 'action-column-header',
            sort: false,
            formatter: (cellContent: any, row: DataRoomDisplayItem) => {
                if (row.type === 'folder' && canUserManagePermissions) {
                    return (
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleTeamAccessRightsClick(row)}>
                            Team Access Rights
                        </Button>
                    );
                }
                if (row.type === 'file' && row.fileView) {
                    return renderFileActions(row.fileView);
                }
                return null;
            },
        },
    ];

    const selectedFilesCount = useMemo(() => {
        return selectionHelper.getSelectedFilePaths().length;
    }, [selectionHelper]);

    return (
        <div className="data-room">
            <div className="data-room-header">
                <div className="data-room-header-top">
                    <Breadcrumb>
                        {breadcrumb.map((item, index) => {
                            const isLast = index === breadcrumb.length - 1;
                            const isRoot = index === 0;
                            const practiceNames =
                                isRoot && practices.length > 0
                                    ? practices.map((p) => p.practiceName)
                                    : [];

                            return (
                                <BreadcrumbItem key={item.id} active={isLast}>
                                    {isLast ? (
                                        <span className="breadcrumb-current">
                                            {isRoot &&
                                            practiceNames.length > 0 ? (
                                                <ShowFirstThenFullTextOnHoverLabel
                                                    items={practiceNames}
                                                    tooltipKey={`breadcrumb-root-${engagementId}`}
                                                />
                                            ) : (
                                                item.name
                                            )}
                                        </span>
                                    ) : (
                                        <a
                                            className="breadcrumb-link"
                                            onClick={() =>
                                                handleBreadcrumbClick(item.id)
                                            }>
                                            {isRoot &&
                                            practiceNames.length > 0 ? (
                                                <ShowFirstThenFullTextOnHoverLabel
                                                    items={practiceNames}
                                                    tooltipKey={`breadcrumb-root-link-${engagementId}`}
                                                />
                                            ) : (
                                                item.name
                                            )}
                                        </a>
                                    )}
                                </BreadcrumbItem>
                            );
                        })}
                    </Breadcrumb>
                    <div className="header-buttons">
                        {canUserRemoveFiles && (
                            <Button
                                color="primary"
                                onClick={() => setIsUploadModalVisible(true)}
                                className="btn btn-primary">
                                Upload File
                            </Button>
                        )}
                        <ButtonLoader
                            buttonText="Download"
                            loaderButtonText=""
                            isLoading={isDownloading}
                            disabled={selectedFilesCount === 0 || isDownloading}
                            onClick={handleDownload}
                            className="btn btn-primary"
                        />
                    </div>
                </div>
            </div>
            <div className="data-room-content">
                <BootstrapTable
                    striped
                    bordered
                    bootstrap4
                    remote={true}
                    keyField="id"
                    data={currentItems}
                    columns={columns}
                    onTableChange={handleTableChange}
                    noDataIndication="No items available"
                    selectRow={{
                        mode: 'checkbox',
                        clickToSelect: false,
                        hideSelectColumn: false,
                        selected: Array.from(selectedPaths),
                        onSelect: () => {
                            return false;
                        },
                        onSelectAll: (
                            isSelect: boolean,
                            rows: DataRoomDisplayItem[]
                        ) => {
                            handleSelectAll(isSelect, rows);
                        },
                        selectionHeaderRenderer: ({
                            mode,
                            checked,
                            indeterminate,
                        }) => (
                            <input
                                type="checkbox"
                                checked={checked || isAllSelected}
                                ref={(input) => {
                                    if (input) {
                                        input.indeterminate =
                                            indeterminate || isIndeterminate;
                                    }
                                }}
                                onChange={(e) => {
                                    handleSelectAll(
                                        e.target.checked,
                                        currentItems
                                    );
                                }}
                            />
                        ),
                        selectionRenderer: ({ checked, rowIndex }) => {
                            const row = currentItems[rowIndex];
                            if (!row) return null;

                            const isRowSelected = selectedPaths.has(
                                row.fullPath
                            );
                            let checkboxState: {
                                checked: boolean;
                                indeterminate: boolean;
                            };

                            if (row.type === 'folder') {
                                checkboxState =
                                    selectionHelper.getSelectionStateForFolder(
                                        row.fullPath
                                    );
                            } else {
                                checkboxState = {
                                    checked: isRowSelected,
                                    indeterminate: false,
                                };
                            }

                            return (
                                <input
                                    type="checkbox"
                                    checked={checkboxState.checked}
                                    ref={(input) => {
                                        if (input) {
                                            input.indeterminate =
                                                checkboxState.indeterminate;
                                        }
                                    }}
                                    onChange={() => {
                                        setSelectedPaths(
                                            (prevSelectedPaths) => {
                                                const tempHelper =
                                                    new VDRDocumentSelectionHelper(
                                                        rootFolder,
                                                        prevSelectedPaths
                                                    );

                                                if (row.type === 'folder') {
                                                    const folderState =
                                                        tempHelper.getSelectionStateForFolder(
                                                            row.fullPath
                                                        );
                                                    const shouldSelect =
                                                        !folderState.checked ||
                                                        folderState.indeterminate;
                                                    return tempHelper.handleFolderSelection(
                                                        row.fullPath,
                                                        shouldSelect
                                                    );
                                                } else {
                                                    const isCurrentlySelected =
                                                        prevSelectedPaths.has(
                                                            row.fullPath
                                                        );
                                                    const currentFolder =
                                                        getCurrentFolder();
                                                    return tempHelper.handleFileSelection(
                                                        row.fullPath,
                                                        !isCurrentlySelected,
                                                        currentFolder?.fullPath
                                                    );
                                                }
                                            }
                                        );
                                    }}
                                />
                            );
                        },
                    }}
                />
            </div>

            {isAccessRightsPopupVisible ? (
                <DataRoomAccessRightsPopup
                    visible={isAccessRightsPopupVisible}
                    folderPathLabel={getFolderPathForDisplay()}
                    folderPath={selectedItemForAccessRights?.fullPath || ''}
                    subFolders={getSubFoldersList()}
                    teamMembers={teamMembers ?? []}
                    engagementId={engagementId}
                    permissions={selectedItemForAccessRights?.permissions || []}
                    buyerUserId={buyerUserId}
                    onClose={handleCloseAccessRightsPopup}
                    onPermissionsUpdated={onPermissionsUpdated}
                />
            ) : (
                <></>
            )}

            <ConfirmationModal
                isVisible={isRemoveConfirmationVisible}
                setIsVisible={setIsRemoveConfirmationVisible}
                header="Remove File"
                confirmationText="Are you sure you want to remove this file from the VDR?"
                onConfirm={handleRemoveFile}
                isLoading={isProcessingAction}
                closeOnConfirm={false}
                nextButtonText="Yes, Remove"
                cancelButtonText="Cancel"
                isConfirmationDanger={true}
            />

            <VDRUploadFileModal
                isVisible={isUploadModalVisible}
                onClose={() => setIsUploadModalVisible(false)}
                onUpload={handleUploadFile}
                rootFolder={rootFolder}
                currentFolderPath={currentFolderPath}
            />
        </div>
    );
};

export default DataRoom;
